import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import InstallPWA from '../components/InstallPWA';
import { 
  Settings, 
  Users, 
  FileText, 
  BarChart3, 
  Upload, 
  Shield, 
  Globe,
  BookOpen,
  School,
  UserPlus,
  Calendar,
  Building,
  LogOut,
  MessageSquare,
  Database,
  Wallet,
  GraduationCap
} from 'lucide-react';

// Import admin dashboard components
import TermManagement from '../components/admin/TermManagement';
import SubscriptionManagement from '../components/admin/SubscriptionManagement';
import AssetManager from '../components/admin/AssetManager';
import SubAdminManagement from '../components/admin/SubAdminManagement';
import LessonNotesStats from '../components/admin/LessonNotesStats';
import AILessonGenerator from '../components/admin/AILessonGenerator';
import SEOManagement from '../components/admin/SEOManagement';
import SiteSettings from '../components/admin/SiteSettings';
import UserManagerFresh from '../components/admin/UserManagerFresh';
import AdminOverview from '../components/admin/AdminOverview';
import HybridLessonGenerator from '../components/admin/HybridLessonGenerator';
import ManageLessonNotes from '../components/admin/ManageLessonNotes';
import BackupRestore from '../components/admin/BackupRestore';
import PromotionQuestionBank from '../components/admin/PromotionQuestionBank';
import DirectorWalletManagement from '../components/admin/DirectorWalletManagement';
import PrivateSectorSubDashboard from '../components/admin/PrivateSectorSubDashboard';

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  const handleLogout = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      await fetch(`${backendUrl}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      
      logout(); // Clear auth context
    } catch (error) {
      console.error('Logout error:', error);
      // Even if backend logout fails, clear local auth
      logout();
    }
  };

  if (!user || (user.role !== 'admin' && user.role !== 'sub-admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-red-600">Access Denied</CardTitle>
            <CardDescription>You don&apos;t have permission to access the admin dashboard.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <InstallPWA />
      {/* Header - Mobile Responsive */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center py-4 sm:py-6 gap-4">
            <div className="w-full sm:w-auto">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-sm sm:text-base text-gray-600">Welcome back, {user.name}</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-4 w-full sm:w-auto">
              <Badge 
                variant={user.role === 'admin' ? 'default' : 'secondary'}
                className="px-2 sm:px-3 py-1 text-xs sm:text-sm"
              >
                {user.role === 'admin' ? 'Super Admin' : 'Sub Admin'}
              </Badge>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleLogout}
                className="text-red-600 border-red-300 hover:bg-red-50 flex-1 sm:flex-initial"
              >
                <LogOut className="h-4 w-4 mr-1 sm:mr-2" />
                <span className="text-xs sm:text-sm">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Mobile Responsive */}
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8 py-4 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4 sm:space-y-8">
          {/* Mobile: Horizontal scrollable tabs, Desktop: Grid */}
          <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
            <TabsList className="inline-flex lg:grid w-auto lg:w-full lg:grid-cols-7 gap-1 p-2 bg-white min-w-max lg:min-w-0 h-auto">
              <TabsTrigger 
                value="overview" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <BarChart3 className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Overview</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="terms" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <Calendar className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Terms</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="subscriptions" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <School className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Subscriptions</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="assets" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <Shield className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Assets</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="admins" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <UserPlus className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Admins</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="ai-lessons" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <BookOpen className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Lesson Writer</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="manage-lessons" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <School className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Manage Lessons</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="users" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <Users className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Users</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="seo" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <Globe className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">SEO</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="wallets" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <Wallet className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Wallets</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="settings" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-blue-50 data-[state=active]:text-blue-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <Settings className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Settings</span>
              </TabsTrigger>
              
              <TabsTrigger 
                value="backup" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-green-50 data-[state=active]:text-green-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <Database className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Backup</span>
              </TabsTrigger>

              <TabsTrigger 
                value="promotions" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <GraduationCap className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Promotions</span>
              </TabsTrigger>

              <TabsTrigger 
                value="private-sector" 
                className="flex flex-col items-center gap-1 p-2 sm:p-3 data-[state=active]:bg-indigo-50 data-[state=active]:text-indigo-600 min-w-[70px] sm:min-w-0 h-auto"
              >
                <Building className="h-5 w-5" />
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap">Private Sector</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <AdminOverview />
          </TabsContent>

          {/* Term Management */}
          <TabsContent value="terms">
            <TermManagement />
          </TabsContent>

          {/* Subscription Management */}
          <TabsContent value="subscriptions">
            <SubscriptionManagement />
          </TabsContent>

          {/* Commissioner Assets */}
          <TabsContent value="assets">
            <AssetManager />
          </TabsContent>

          {/* Sub-Admin Management */}
          <TabsContent value="admins">
            <SubAdminManagement />
          </TabsContent>

          {/* AI Lesson Writer Tab - Now with Hybrid Approach */}
          <TabsContent value="ai-lessons" className="space-y-6">
            <HybridLessonGenerator />
          </TabsContent>

          {/* Manage Lesson Notes */}
          <TabsContent value="manage-lessons" className="space-y-6">
            <ManageLessonNotes />
          </TabsContent>

          {/* User Management */}
          <TabsContent value="users">
            <UserManagerFresh />
          </TabsContent>

          {/* SEO Management */}
          <TabsContent value="seo">
            <SEOManagement />
          </TabsContent>

          {/* Site Settings */}
          <TabsContent value="settings">
            <SiteSettings />
          </TabsContent>

          {/* Director Wallet Management */}
          <TabsContent value="wallets">
            <DirectorWalletManagement />
          </TabsContent>

          {/* Backup & Restore */}
          <TabsContent value="backup">
            <BackupRestore />
          </TabsContent>

          {/* Promotion Question Bank */}
          <TabsContent value="promotions">
            <PromotionQuestionBank />
          </TabsContent>

          {/* Private Sector Subscription */}
          <TabsContent value="private-sector">
            <PrivateSectorSubDashboard />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;